import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * @typedef {{
 *   id: string,
 *   text: string,
 *   icon: any,
 *   link?: string,
 *   section?: 'primary' | 'secondary',
 * }} MenuItem
 */

const iconPx = 28;

const menuItems = /** @type {MenuItem[]} */ ([
  { id: 'home', text: 'Home', icon: HomeRoundedIcon, link: '/dashboard', section: 'primary' },
  { id: 'patients', text: 'Patients', icon: PeopleRoundedIcon, link: '/patients', section: 'primary' },
  { id: 'hospitals', text: 'Hospitals', icon: LocalHospitalRoundedIcon, link: '/hospitals', section: 'primary' },
  { id: 'users', text: 'Users', icon: AccountCircleIcon, link: '/users', section: 'primary' },
  { id: 'errors', text: 'Errors', icon: ErrorOutlineRoundedIcon, link: '/errors', section: 'primary' },
  { id: 'about', text: 'About', icon: InfoIcon, link: '/about', section: 'secondary' }
]);

/** Determine selected state */
function isItemSelected(item, path, aboutOpen) {
  if (item.id === 'about') return aboutOpen;

  if (item.link === '/dashboard') {
    return path === '/dashboard' || path.startsWith('/dashboard');
  }

  return item.link
    ? path === item.link || path.startsWith(item.link + '/')
    : false;
}

/**
 * @param {{
 *   setPage?: (value: string) => void,
 *   isAdmin?: boolean,
 *   collapsed?: boolean,
 *   onAboutClick?: () => void,
 *   aboutOpen?: boolean
 * }} props
 */
export default function MenuContent({
  setPage,
  isAdmin = false,
  collapsed = false,
  onAboutClick,
  aboutOpen = false
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname || '';

  const containerMin = collapsed ? 0 : 45;
  const hitAreaHeight = collapsed ? 56 : 48;
  const highlightSize = collapsed ? 45 : '100%';

  const primaryItems = menuItems.filter(i => i.section === 'primary');
  const secondaryItems = menuItems.filter(i => i.section === 'secondary');

  const handleClick = (item) => {
    if (item.id === 'about' && typeof onAboutClick === 'function') {
      onAboutClick();
      return;
    }

    if (item.link) {
      navigate(item.link);
      setPage?.(item.link);
    }
  };

  const renderItem = (item) => {
    const selected = isItemSelected(item, path, aboutOpen);

    return (
      <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          selected={selected}
          onClick={() => handleClick(item)}
          sx={{
            px: collapsed ? 1 : 3,
            py: collapsed ? 1 : 1,
            justifyContent: 'center',
            position: 'relative',
            minHeight: hitAreaHeight,
            '&.Mui-selected': {
              backgroundColor: 'transparent !important',
            },
            '&.Mui-selected:before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: highlightSize,
              height: highlightSize,
              bgcolor: 'action.selected',
              borderRadius: collapsed ? 1 : 0,
              zIndex: 1,
            },
          }}
          title={collapsed ? item.text : undefined}
        >
          <ListItemIcon
            sx={{
              minWidth: containerMin,
              justifyContent: 'center',
              color: 'inherit'
            }}
          >
            <item.icon
              style={{
                fontSize: iconPx,
                width: iconPx,
                height: iconPx
              }}
            />
          </ListItemIcon>

          {!collapsed && <ListItemText primary={item.text} />}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Stack sx={{ flexGrow: 1, p: 2, justifyContent: 'space-between' }}>
      <List dense>
        {primaryItems
          .filter(item => (item.id !== 'users' && item.id !== 'hospitals' && item.id !== 'errors') || isAdmin)
          .map(renderItem)}

        {secondaryItems.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            {secondaryItems.map(renderItem)}
          </>
        )}
      </List>

      <Stack sx={{ flexGrow: 1 }} />
    </Stack>
  );
}
